import { supabase } from '../lib/supabase';



export const violationDetectionService = {
  // ============================================================================
  // REAL-TIME VIOLATION DETECTION
  // ============================================================================

  async detectPayrollViolations(payrollRunId) {
    try {
      const { data, error } = await supabase
        ?.rpc('detect_payroll_violations', {
          p_payroll_run_id: payrollRunId
        });
      
      if (error) throw error;
      
      return {
        violationCount: data?.[0]?.violation_count || 0,
        criticalCount: data?.[0]?.critical_count || 0,
        violationsDetected: data?.[0]?.violations_detected || []
      };
    } catch (error) {
      console.error('Error detecting payroll violations:', error);
      throw error;
    }
  },

  async createInstantViolationAlert(violationId, triggerType, affectedEmployeeIds, estimatedCost) {
    try {
      const { data, error } = await supabase
        ?.rpc('create_instant_violation_alert', {
          p_violation_id: violationId,
          p_trigger_type: triggerType,
          p_affected_employee_ids: affectedEmployeeIds,
          p_estimated_cost: estimatedCost
        });
      
      if (error) throw error;
      
      return data;
    } catch (error) {
      console.error('Error creating instant violation alert:', error);
      throw error;
    }
  },

  // ============================================================================
  // VIOLATION DETECTION LOG
  // ============================================================================

  async getAllDetectionLogs() {
    const { data, error } = await supabase
      ?.from('violation_detection_log')
      ?.select('*')
      ?.order('detection_timestamp', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      violationId: row?.violation_id,
      triggerType: row?.trigger_type,
      detectionSource: row?.detection_source,
      detectionTimestamp: row?.detection_timestamp,
      ruleViolated: row?.rule_violated,
      expectedValue: row?.expected_value,
      actualValue: row?.actual_value,
      varianceAmount: row?.variance_amount,
      variancePercentage: row?.variance_percentage,
      affectedEmployeeIds: row?.affected_employee_ids || [],
      affectedPayrollRunId: row?.affected_payroll_run_id,
      affectedDepartment: row?.affected_department,
      alertSent: row?.alert_sent,
      alertSentAt: row?.alert_sent_at,
      adminNotified: row?.admin_notified,
      clientNotified: row?.client_notified,
      confidenceScore: row?.confidence_score,
      detectionMethod: row?.detection_method,
      metadata: row?.metadata,
      createdAt: row?.created_at
    })) || [];
  },

  async getDetectionLogsByTriggerType(triggerType) {
    const { data, error } = await supabase
      ?.from('violation_detection_log')
      ?.select('*')
      ?.eq('trigger_type', triggerType)
      ?.order('detection_timestamp', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      violationId: row?.violation_id,
      detectionTimestamp: row?.detection_timestamp,
      ruleViolated: row?.rule_violated,
      varianceAmount: row?.variance_amount,
      affectedEmployeeIds: row?.affected_employee_ids || [],
      alertSent: row?.alert_sent
    })) || [];
  },

  // ============================================================================
  // VIOLATION PATTERN ANALYSIS
  // ============================================================================

  async getAllPatterns() {
    const { data, error } = await supabase
      ?.from('violation_pattern_analysis')
      ?.select('*')
      ?.order('occurrence_frequency', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      patternName: row?.pattern_name,
      violationTriggerType: row?.violation_trigger_type,
      occurrenceFrequency: row?.occurrence_frequency,
      affectedDepartments: row?.affected_departments || [],
      commonRootCauses: row?.common_root_causes || [],
      trendDirection: row?.trend_direction,
      trendPercentageChange: row?.trend_percentage_change,
      firstDetectedAt: row?.first_detected_at,
      lastDetectedAt: row?.last_detected_at,
      preventionStrategy: row?.prevention_strategy,
      automationOpportunity: row?.automation_opportunity,
      estimatedPreventionCostKina: row?.estimated_prevention_cost_kina,
      estimatedViolationCostKina: row?.estimated_violation_cost_kina,
      roiPercentage: row?.roi_percentage,
      patternData: row?.pattern_data,
      createdAt: row?.created_at
    })) || [];
  },

  async getPatternsByTriggerType(triggerType) {
    const { data, error } = await supabase
      ?.from('violation_pattern_analysis')
      ?.select('*')
      ?.eq('violation_trigger_type', triggerType)
      ?.order('occurrence_frequency', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      patternName: row?.pattern_name,
      occurrenceFrequency: row?.occurrence_frequency,
      trendDirection: row?.trend_direction,
      preventionStrategy: row?.prevention_strategy,
      roiPercentage: row?.roi_percentage
    })) || [];
  },

  // ============================================================================
  // CLIENT RISK SCORING
  // ============================================================================

  async calculateClientRiskScore(companyId) {
    try {
      const { data, error } = await supabase
        ?.rpc('calculate_client_risk_score', {
          p_company_id: companyId
        });
      
      if (error) throw error;
      
      return data;
    } catch (error) {
      console.error('Error calculating client risk score:', error);
      throw error;
    }
  },

  async getAllClientRiskScores() {
    const { data, error } = await supabase
      ?.from('client_risk_scores')
      ?.select(`
        *,
        company:company_settings(company_name, company_abbreviation)
      `)
      ?.order('overall_risk_score', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      companyId: row?.company_id,
      companyName: row?.company?.company_name,
      companyAbbreviation: row?.company?.company_abbreviation,
      overallRiskScore: row?.overall_risk_score,
      riskLevel: row?.risk_level,
      previousRiskScore: row?.previous_risk_score,
      riskTrend: row?.risk_trend,
      violationFrequencyScore: row?.violation_frequency_score,
      violationSeverityScore: row?.violation_severity_score,
      remediationComplianceScore: row?.remediation_compliance_score,
      totalViolations30Days: row?.total_violations_30_days,
      criticalViolations30Days: row?.critical_violations_30_days,
      unresolvedViolations: row?.unresolved_violations,
      averageResolutionDays: row?.average_resolution_days,
      totalViolationCostKina: row?.total_violation_cost_kina,
      totalPreventionSavingsKina: row?.total_prevention_savings_kina,
      interventionPriority: row?.intervention_priority,
      recommendedActions: row?.recommended_actions || [],
      updatedAt: row?.updated_at
    })) || [];
  },

  async getClientRiskScoreByCompany(companyId) {
    const { data, error } = await supabase
      ?.from('client_risk_scores')
      ?.select('*')
      ?.eq('company_id', companyId)
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      companyId: data?.company_id,
      overallRiskScore: data?.overall_risk_score,
      riskLevel: data?.risk_level,
      totalViolations30Days: data?.total_violations_30_days,
      criticalViolations30Days: data?.critical_violations_30_days,
      unresolvedViolations: data?.unresolved_violations,
      recommendedActions: data?.recommended_actions || []
    };
  },

  // ============================================================================
  // VIOLATION FORECASTS
  // ============================================================================

  async getAllForecasts() {
    const { data, error } = await supabase
      ?.from('violation_forecasts')
      ?.select(`
        *,
        company:company_settings(company_name)
      `)
      ?.order('forecast_start_date', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      companyId: row?.company_id,
      companyName: row?.company?.company_name,
      forecastPeriod: row?.forecast_period,
      forecastStartDate: row?.forecast_start_date,
      forecastEndDate: row?.forecast_end_date,
      violationTriggerType: row?.violation_trigger_type,
      predictedOccurrenceCount: row?.predicted_occurrence_count,
      predictedAffectedEmployees: row?.predicted_affected_employees,
      predictedCostImpactKina: row?.predicted_cost_impact_kina,
      confidenceLevel: row?.confidence_level,
      patternIndicators: row?.pattern_indicators || [],
      earlyWarningTriggered: row?.early_warning_triggered,
      preventionRecommendations: row?.prevention_recommendations || [],
      estimatedPreventionCostKina: row?.estimated_prevention_cost_kina,
      createdAt: row?.created_at
    })) || [];
  },

  // ============================================================================
  // REMEDIATION WORKFLOWS
  // ============================================================================

  async getReadyRemediationWorkflows() {
    const { data, error } = await supabase
      ?.from('remediation_workflows')
      ?.select(`
        *,
        violation:compliance_violations(violation_type, severity, description, affected_count),
        remediation_action:remediation_actions(description, backpay_amount, priority)
      `)
      ?.eq('workflow_status', 'ready')
      ?.eq('is_one_click', true)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      violationId: row?.violation_id,
      remediationActionId: row?.remediation_action_id,
      workflowName: row?.workflow_name,
      actionType: row?.action_type,
      isOneClick: row?.is_one_click,
      workflowStatus: row?.workflow_status,
      executionSteps: row?.execution_steps,
      currentStep: row?.current_step,
      totalSteps: row?.total_steps,
      estimatedCostKina: row?.estimated_cost_kina,
      estimatedTimeMinutes: row?.estimated_time_minutes,
      affectedEmployeeCount: row?.affected_employee_count,
      violation: {
        violationType: row?.violation?.violation_type,
        severity: row?.violation?.severity,
        description: row?.violation?.description,
        affectedCount: row?.violation?.affected_count
      },
      remediationAction: {
        description: row?.remediation_action?.description,
        backpayAmount: row?.remediation_action?.backpay_amount,
        priority: row?.remediation_action?.priority
      },
      createdAt: row?.created_at
    })) || [];
  },

  async executeOneClickRemediation(workflowId, initiatedBy) {
    try {
      const { data, error } = await supabase
        ?.rpc('execute_one_click_remediation', {
          p_workflow_id: workflowId,
          p_initiated_by: initiatedBy
        });
      
      if (error) throw error;
      
      return data;
    } catch (error) {
      console.error('Error executing one-click remediation:', error);
      throw error;
    }
  },

  async getWorkflowById(workflowId) {
    const { data, error } = await supabase
      ?.from('remediation_workflows')
      ?.select(`
        *,
        violation:compliance_violations(*),
        remediation_action:remediation_actions(*)
      `)
      ?.eq('id', workflowId)
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      workflowName: data?.workflow_name,
      actionType: data?.action_type,
      workflowStatus: data?.workflow_status,
      executionSteps: data?.execution_steps,
      estimatedCostKina: data?.estimated_cost_kina,
      success: data?.success,
      errorMessage: data?.error_message,
      initiatedBy: data?.initiated_by,
      initiatedAt: data?.initiated_at,
      completedAt: data?.completed_at,
      violation: data?.violation,
      remediationAction: data?.remediation_action
    };
  },

  // ============================================================================
  // ANALYTICS & REPORTING
  // ============================================================================

  async getViolationAnalyticsSummary(periodStart, periodEnd) {
    try {
      const { data, error } = await supabase
        ?.rpc('get_violation_analytics_summary', {
          p_period_start: periodStart,
          p_period_end: periodEnd
        });
      
      if (error) throw error;
      
      return {
        totalViolations: data?.[0]?.total_violations || 0,
        criticalViolations: data?.[0]?.critical_violations || 0,
        violationsPrevented: data?.[0]?.violations_prevented || 0,
        preventionSuccessRate: data?.[0]?.prevention_success_rate || 0,
        totalCostAvoidedKina: data?.[0]?.total_cost_avoided_kina || 0,
        avgDetectionTimeHours: data?.[0]?.avg_detection_time_hours || 0,
        topViolationTypes: data?.[0]?.top_violation_types || []
      };
    } catch (error) {
      console.error('Error getting violation analytics summary:', error);
      throw error;
    }
  },

  async getPreventionMetrics() {
    const { data, error } = await supabase
      ?.from('violation_prevention_metrics')
      ?.select('*')
      ?.order('period_start', { ascending: false })
      ?.limit(12);
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      metricPeriod: row?.metric_period,
      periodStart: row?.period_start,
      periodEnd: row?.period_end,
      violationsPrevented: row?.violations_prevented,
      earlyDetections: row?.early_detections,
      successfulRemediations: row?.successful_remediations,
      failedRemediations: row?.failed_remediations,
      preventionCostKina: row?.prevention_cost_kina,
      violationCostAvoidedKina: row?.violation_cost_avoided_kina,
      netSavingsKina: row?.net_savings_kina,
      roiPercentage: row?.roi_percentage,
      detectionAccuracyPercentage: row?.detection_accuracy_percentage,
      remediationSuccessRate: row?.remediation_success_rate,
      averageDetectionTimeHours: row?.average_detection_time_hours,
      averageRemediationTimeHours: row?.average_remediation_time_hours,
      trendVsPreviousPeriod: row?.trend_vs_previous_period,
      improvementPercentage: row?.improvement_percentage,
      createdAt: row?.created_at
    })) || [];
  },

  // ============================================================================
  // REALTIME SUBSCRIPTIONS
  // ============================================================================

  subscribeToViolationDetections(callback) {
    const channel = supabase
      ?.channel('violation_detections_channel')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'violation_detection_log'
        },
        (payload) => {
          const newDetection = {
            id: payload?.new?.id,
            violationId: payload?.new?.violation_id,
            triggerType: payload?.new?.trigger_type,
            detectionSource: payload?.new?.detection_source,
            detectionTimestamp: payload?.new?.detection_timestamp,
            ruleViolated: payload?.new?.rule_violated,
            varianceAmount: payload?.new?.variance_amount,
            affectedEmployeeIds: payload?.new?.affected_employee_ids || [],
            alertSent: payload?.new?.alert_sent
          };
          callback(newDetection);
        }
      )
      ?.subscribe();
    
    return channel;
  },

  unsubscribeFromViolationDetections(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  subscribeToRemediationWorkflows(callback) {
    const channel = supabase
      ?.channel('remediation_workflows_channel')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'remediation_workflows'
        },
        (payload) => {
          const workflow = {
            id: payload?.new?.id,
            workflowName: payload?.new?.workflow_name,
            workflowStatus: payload?.new?.workflow_status,
            actionType: payload?.new?.action_type,
            success: payload?.new?.success,
            completedAt: payload?.new?.completed_at
          };
          callback(workflow, payload?.eventType);
        }
      )
      ?.subscribe();
    
    return channel;
  },

  unsubscribeFromRemediationWorkflows(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};

export default violationDetectionService;